import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { BookOpen, BriefcaseBusiness, CalendarDays, CheckSquare, FolderKanban, Search, Target, UserRound } from "lucide-react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

const TYPE_META = {
  CLIENT: { icon: BriefcaseBusiness, color: "text-emerald-500", background: "bg-emerald-500/10" },
  PROJECT: { icon: FolderKanban, color: "text-primary", background: "bg-primary/10" },
  TASK: { icon: CheckSquare, color: "text-blue-500", background: "bg-blue-500/10" },
  EVENT: { icon: CalendarDays, color: "text-pink-500", background: "bg-pink-500/10" },
  LEAD: { icon: Target, color: "text-amber-500", background: "bg-amber-500/10" },
  USER: { icon: UserRound, color: "text-violet-500", background: "bg-violet-500/10" },
  KNOWLEDGE: { icon: BookOpen, color: "text-indigo-500", background: "bg-indigo-500/10" },
};

export function GlobalSearch() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const { data: results = [], isFetching } = useQuery({
    queryKey: ["global-search", query],
    queryFn: async () => (await api.get("/search", { params: { q: query } })).data,
    enabled: open && query.trim().length >= 2,
  });

  useEffect(() => {
    const handleShortcut = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setOpen((current) => !current);
      }
    };
    window.addEventListener("keydown", handleShortcut);
    return () => window.removeEventListener("keydown", handleShortcut);
  }, []);

  const selectResult = (result) => {
    setOpen(false);
    setQuery("");
    navigate(result.link);
  };

  return (
    <>
      <button onClick={() => setOpen(true)} className="hidden w-[260px] items-center gap-2 rounded-lg border border-border bg-background px-3 py-2 text-sm text-muted-foreground transition-colors hover:border-primary/40 lg:flex">
        <Search className="h-4 w-4" /><span className="flex-1 text-left">Pesquisar no CRM</span><kbd className="rounded border border-border bg-muted px-1.5 py-0.5 text-[10px]">Ctrl K</kbd>
      </button>
      <button onClick={() => setOpen(true)} className="rounded-lg p-2 text-muted-foreground hover:bg-muted lg:hidden" aria-label="Pesquisar"><Search className="h-[18px] w-[18px]" /></button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl gap-0 overflow-hidden p-0">
          <DialogHeader className="sr-only"><DialogTitle>Pesquisa global</DialogTitle><DialogDescription>Pesquisa em todo o CRM.</DialogDescription></DialogHeader>
          <div className="relative border-b border-border"><Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" /><Input autoFocus value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Pesquisar clientes, projetos, tarefas, eventos ou pessoas..." className="h-14 rounded-none border-0 pl-12 pr-4 text-base shadow-none focus-visible:ring-0" /></div>
          <div className="max-h-[440px] overflow-y-auto p-2">
            {query.trim().length < 2 && <div className="px-4 py-12 text-center text-sm text-muted-foreground">Escreve pelo menos dois caracteres para pesquisar em todo o CRM.</div>}
            {query.trim().length >= 2 && isFetching && <div className="px-4 py-8 text-center text-sm text-muted-foreground">A pesquisar...</div>}
            {query.trim().length >= 2 && !isFetching && results.length === 0 && <div className="px-4 py-12 text-center text-sm text-muted-foreground">Nenhum resultado encontrado.</div>}
            {results.map((result) => {
              const meta = TYPE_META[result.type] || TYPE_META.TASK;
              const Icon = meta.icon;
              return <button key={`${result.type}-${result.id}`} onClick={() => selectResult(result)} className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left hover:bg-muted"><div className={`flex h-9 w-9 items-center justify-center rounded-lg ${meta.background}`}><Icon className={`h-4 w-4 ${meta.color}`} /></div><div className="min-w-0 flex-1"><div className="truncate text-sm font-medium">{result.title}</div><div className="truncate text-xs text-muted-foreground">{result.subtitle}</div></div></button>;
            })}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
